import { Button, LinkButton, Tooltip } from '@cambridgeassessment/cambridge-ui';
import { CentreBase, ConsortiumCentres, ModeratorInfo } from '@cambridgeassessment/lib-ee-commons';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  setPostResultsReviewModerator,
  unallocatePostResultsReviewModerator,
} from '../../../../store/postResults/thunks';
import { PostResultsParams } from '../../../../types/PostResults';
import { StyledInputContainer } from '../../Content.styles';
import {
  StyledCentreListItem,
  StyledCentreTitle,
  StyledCentreViewContainer,
  StyledOrderedList,
  StyledParagraph,
  StyledSaveButtonContainer,
  StyledSmartSearch,
  StyledUnmapModeratorLinkContainer as StyledUnallocateModeratorLinkContainer,
} from './PostResultsPage.styles';
import { SmartModeratorSearch } from './SmartModeratorSearch';

interface Props {
  queryParams: PostResultsParams;
  centre?: CentreBase[];
  consortium?: ConsortiumCentres;
  moderators: ModeratorInfo[];
  allocatedModerator?: ModeratorInfo;
}

const CentreViewContainer = (props: Props): JSX.Element => {
  const dispatch = useDispatch();
  const { centre, consortium, moderators, allocatedModerator, queryParams } = props;
  const [selectedModerator, setSelectedModerator] = useState<ModeratorInfo | null>(null);
  const isCentre = !!centre;
  const isConsortium = !!consortium;
  const isModeratorAllocated = !!allocatedModerator;

  const handleModeratorSelection = (moderator: ModeratorInfo | null): void => {
    setSelectedModerator(moderator);
  };

  const handleSaveButton = (): void => {
    console.log(queryParams);
    !!selectedModerator && dispatch(setPostResultsReviewModerator(queryParams, selectedModerator));
  };

  const handleModeratorUnallocation = (): void => {
    if (selectedModerator) {
      dispatch(unallocatePostResultsReviewModerator(queryParams, selectedModerator.creditorNumber));
    }
  };

  return (
    <StyledCentreViewContainer data-qa-id={'centre-view-conainer'}>
      {isCentre && (
        <StyledCentreTitle data-qa-id={'centre-title'}>
          {centre[0].id} {centre[0].name}
        </StyledCentreTitle>
      )}
      {isConsortium && (
        <>
          <StyledCentreTitle data-qa-id={'consortium-title'}>{consortium.consortiaName}</StyledCentreTitle>
          <StyledOrderedList>
            {consortium.centres?.map((singleCentre) => (
              <StyledCentreListItem key={singleCentre.id} data-qa-id={`consortium-centre-${singleCentre.id}`}>
                {singleCentre.id} - {singleCentre.name}
              </StyledCentreListItem>
            ))}
          </StyledOrderedList>
        </>
      )}
      <StyledParagraph>
        You can associate moderators with a centre/Consortium for conducting post-results reviews. Please note that the
        original moderator is not included in the search results.
      </StyledParagraph>
      <StyledInputContainer>
        <StyledParagraph>Moderator for the Post Results Review</StyledParagraph>
        <StyledSmartSearch>
          <SmartModeratorSearch
            moderators={moderators}
            onModeratorSelect={handleModeratorSelection}
            allocatedModerator={allocatedModerator}
          ></SmartModeratorSearch>
        </StyledSmartSearch>
        {isModeratorAllocated && (
          <StyledUnallocateModeratorLinkContainer>
            <LinkButton dataQaId='unallocate-moderator' onClick={handleModeratorUnallocation} endIcon={false}>
              Unallocate moderator
            </LinkButton>
          </StyledUnallocateModeratorLinkContainer>
        )}

        {!isModeratorAllocated && !!selectedModerator && (
          <Tooltip
            arrow={true}
            dataQaId='save-moderator-tooltip'
            title='An email will be sent by the system to the allocated moderator'
            placement='top'
            enterDelay={500}
          >
            <StyledSaveButtonContainer>
              <Button qaId='save-moderator-button' onClick={handleSaveButton}>
                Save
              </Button>
            </StyledSaveButtonContainer>
          </Tooltip>
        )}
      </StyledInputContainer>
    </StyledCentreViewContainer>
  );
};

export default CentreViewContainer;
